import type { ModeTransmission } from '@admin-pmvb/core'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { type FichePiece, requetes } from '../api'
import {
  LIBELLES_DEPOT_DIRECT,
  LIBELLES_RANGEMENT,
  LIBELLES_TRANSMISSION,
  LIBELLES_TYPE_PIECE,
} from '../libelles'
import { type Acheminable, BadgeAcheminement, DecisionsAcheminement } from './Acheminement'
import { portailDuMode } from './bilan-justificatifs'
import { ClasserPiece } from './ClasserPiece'
import { Icone, type NomIcone } from './Icone'
import { RattacherPiece } from './RattacherPiece'

// Assistant de la fiche d'une pièce : ce qu'il reste à en faire, étape par étape. Affecter
// (entité et type) est le seul geste, classer quand la lecture hésite sur la rubrique ; le
// rangement dans le Drive et l'envoi au cabinet
// suivent seuls (règles de packages/core), l'assistant montre où ils en sont et propose les
// décisions qui leur manquent. Une pièce prise à tort s'écarte (rejet, suppression). Entité en
// dépôt direct (Tiime) : rien ne part par mail, la pièce attend son opération chez le portail,
// ou y est déjà (reconnue sur une opération déjà justifiée), ou l'agent du Mac l'y dépose puis la
// relie (dépôt en cours, déposée avec son numéro de document, reliée à l'opération du jour dit,
// dépôt interrompu à vérifier).

type EtatEtape = 'faite' | 'active' | 'attente' | 'alerte'

const MARQUE: Record<EtatEtape, string> = {
  faite: 'Fait',
  active: 'À faire',
  attente: 'Plus tard',
  alerte: 'En échec',
}

function Etape({
  numero,
  icone,
  titre,
  etat,
  children,
}: {
  numero: number
  icone: NomIcone
  titre: string
  etat: EtatEtape
  children: ReactNode
}) {
  return (
    <li className={`etape-assistant etape-${etat}`}>
      <span className="etape-pastille" aria-hidden="true">
        {etat === 'faite' ? <Icone nom="check" taille={16} /> : numero}
      </span>
      <div className="etape-contenu">
        <h3>
          <Icone nom={icone} taille={18} /> {titre}
          <span className="visuellement-cache"> : {MARQUE[etat]}</span>
        </h3>
        {children}
      </div>
    </li>
  )
}

/** Dépôt direct chez le portail du cabinet : sa ligne au registre, et le portail du mode. */
type DepotDirect = { ligne: FichePiece['depotDirect']; portail: string | null }

/** Ce que devient la pièce au cabinet, en une phrase. */
function texteCabinet(p: Acheminable, depot: DepotDirect) {
  // Inscrite au registre des justificatifs : « déjà dans Tiime » (reconnue), en cours, déposée
  // (document n° …), reliée à l'opération du …, interrompue.
  if (depot.ligne) return LIBELLES_DEPOT_DIRECT[depot.ligne.statut](depot.ligne)
  if (p.transmission === 'faite' && p.transmiseLe)
    return `Envoyée à ${p.transmiseA ?? 'l’adresse de collecte'}`
  if (p.dejaChezCabinet) return 'Déjà reçue par sa plateforme agréée : pas de renvoi.'
  if (p.reglement === 'avance')
    return 'Avance personnelle : rien au cabinet, la note de frais se fait par lot (À refacturer).'
  // Entité en dépôt direct : rien ne part par mail, la pièce attend son opération.
  if (depot.portail && p.transmission === 'a_faire')
    return `Attend son opération dans ${depot.portail} : déposée et reliée seule quand le rapprochement est sûr et l’écriture ouverte, sinon proposée dans le bilan des justificatifs. Rien ne part par mail.`
  if (p.transmission && LIBELLES_TRANSMISSION[p.transmission])
    return LIBELLES_TRANSMISSION[p.transmission]
  return 'Rien à envoyer au cabinet.'
}

function etatDrive(p: Acheminable, affectee: boolean): EtatEtape {
  if (!affectee) return 'attente'
  if (p.rangement === 'fait') return 'faite'
  if (p.etat === 'drive_echec' || p.rangementErreur) return 'alerte'
  return 'active'
}

function etatCabinet(p: Acheminable, affectee: boolean, depot: DepotDirect): EtatEtape {
  if (!affectee) return 'attente'
  if (p.etat === 'tiime_echec') return 'alerte'
  if (
    p.transmission === 'faite' ||
    p.dejaChezCabinet ||
    p.reglement === 'avance' ||
    p.transmission === null ||
    p.transmission === 'sans_objet'
  )
    return 'faite'
  if (p.transmissionErreur && p.transmission === 'a_faire') return 'alerte'
  // Dépôt direct : rien à faire pour un humain, la pièce attend son opération chez le portail,
  // ou l'agent du Mac est en train de la déposer.
  if (
    depot.portail &&
    p.transmission === 'a_faire' &&
    (!depot.ligne || depot.ligne.statut === 'reserve')
  )
    return 'attente'
  return 'active'
}

export function AssistantPiece({
  p,
  ecriture,
  ecrivables,
  supprimable,
  onRejeter,
  onSupprimer,
}: {
  p: FichePiece
  ecriture: boolean
  ecrivables: { id: string; nom: string }[]
  supprimable: boolean
  onRejeter: () => void
  onSupprimer: () => void
}) {
  const affectee = p.statut === 'rattachee'
  const [corriger, setCorriger] = useState(false)
  const peutAffecter = ecriture && ecrivables.length > 0
  // Mode de transmission de l'entité (liste déjà lue par la fiche) : dépôt direct ou mail.
  const entites = useQuery(requetes.entites).data
  const mode: ModeTransmission | undefined = entites?.find((e) => e.id === p.entiteId)?.transmission
  const depot: DepotDirect = {
    ligne: p.depotDirect,
    portail: affectee && mode ? portailDuMode(mode) : null,
  }
  const drive = etatDrive(p, affectee)
  const cabinet = etatCabinet(p, affectee, depot)

  return (
    <section className="carte section assistant-piece">
      <div className="section-titre">
        <h2>Assistant</h2>
        {affectee && <BadgeAcheminement etat={p.etat} />}
      </div>
      <ol className="etapes-assistant">
        <Etape numero={1} icone="domain" titre="Affecter" etat={affectee ? 'faite' : 'active'}>
          {affectee && !corriger && (
            <p>
              {p.entite} · {LIBELLES_TYPE_PIECE[p.type]}
              {peutAffecter && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="bouton-discret"
                    onClick={() => setCorriger(true)}
                  >
                    Corriger
                  </button>
                </>
              )}
            </p>
          )}
          {!affectee && (
            <p className="discret">
              {p.statut === 'ignoree'
                ? 'Pièce rejetée. L’affecter la remet dans le circuit.'
                : 'Choisir l’entité et le type : le rangement et l’envoi au cabinet suivent seuls.'}
            </p>
          )}
          {peutAffecter && (!affectee || corriger) && (
            <RattacherPiece
              p={p}
              ecrivables={ecrivables}
              libelleBouton={affectee ? 'Corriger' : 'Affecter'}
              onFait={() => setCorriger(false)}
            />
          )}
          {affectee && corriger && (
            <button type="button" className="bouton-discret" onClick={() => setCorriger(false)}>
              Annuler
            </button>
          )}
          {!ecriture && !affectee && <p className="discret">À affecter par un administrateur.</p>}
        </Etape>

        <Etape numero={2} icone="inventory_2" titre="Ranger dans le Drive" etat={drive}>
          {!affectee ? (
            <p className="discret">
              Une fois affectée, rangée seule dans le Drive de l’entité, à sa place dans le plan de
              classement (dossier permanent, ou exercice et cycle), sous un nom normalisé.
            </p>
          ) : (
            <>
              <ClasserPiece p={p} ecriture={ecriture} />
              <p>{p.rangement ? LIBELLES_RANGEMENT[p.rangement] : 'Pas encore rangée'}</p>
              {p.cheminDrive && (
                <p className="discret">
                  {p.lienDrive ? (
                    <a href={p.lienDrive} target="_blank" rel="noreferrer">
                      {p.cheminDrive}
                    </a>
                  ) : (
                    p.cheminDrive
                  )}
                </p>
              )}
              {p.rangement !== 'fait' && p.rangementErreur && (
                <p className="alerte">
                  {p.rangementErreur}
                  {p.rangementTentatives > 0 && ` (${p.rangementTentatives} essai(s))`}
                </p>
              )}
            </>
          )}
        </Etape>

        <Etape numero={3} icone="forward_to_inbox" titre="Envoyer à la compta" etat={cabinet}>
          {!affectee ? (
            <p className="discret">
              Factures d’achat seulement : envoyées seules à l’adresse de collecte du cabinet.
            </p>
          ) : (
            <>
              <p>{texteCabinet(p, depot)}</p>
              {p.boite && p.reglement && (
                <p className="discret">
                  {p.reglement === 'societe' ? 'Réglée par la société' : 'Avancée personnellement'}
                </p>
              )}
              {p.transmission !== 'faite' && p.transmissionErreur && (
                <p className={p.transmission === 'sans_objet' ? 'discret' : 'alerte'}>
                  {p.transmissionErreur}
                  {p.transmissionTentatives > 0 && ` (${p.transmissionTentatives} essai(s))`}
                </p>
              )}
            </>
          )}
        </Etape>
      </ol>

      {affectee && ecriture && <DecisionsAcheminement p={p} />}

      {ecriture && (p.statut !== 'ignoree' || supprimable) && (
        <div className="assistant-ecarter">
          <span className="discret">Prise à tort (sans rapport, en double) ?</span>
          {p.statut !== 'ignoree' && (
            <button type="button" className="bouton-discret bouton-danger" onClick={onRejeter}>
              <Icone nom="delete" /> Rejeter
            </button>
          )}
          {supprimable && (
            <button type="button" className="bouton-discret bouton-danger" onClick={onSupprimer}>
              <Icone nom="delete" /> Supprimer
            </button>
          )}
        </div>
      )}
    </section>
  )
}
